import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../services/api';
import type { Organization, ManualCost } from '../types';

// Trailers
export function useTrailers() {
  return useQuery({
    queryKey: ['trailers'],
    queryFn: () => api.getTrailers(),
  });
}

export function useTrailer(id: string) {
  return useQuery({
    queryKey: ['trailer', id],
    queryFn: () => api.getTrailer(id),
    enabled: !!id,
  });
}

export function useTrailerCargos(trailerId: string, from: string, to: string) {
  return useQuery({
    queryKey: ['trailer-cargos', trailerId, from, to],
    queryFn: () => api.getTrailerCargos(trailerId, from, to),
    enabled: !!trailerId && !!from && !!to,
  });
}

// Analytics
export function useOverview(from?: string, to?: string, period?: string) {
  return useQuery({
    queryKey: ['overview', from, to, period],
    queryFn: () => api.getOverview(from, to, period),
  });
}

export function useTrailerEconomics(from: string, to: string) {
  return useQuery({
    queryKey: ['trailer-economics', from, to],
    queryFn: () => api.getTrailerEconomicsList(from, to),
    enabled: !!from && !!to,
  });
}

export function useTrailerHourEfficiency(from: string, to: string) {
  return useQuery({
    queryKey: ['trailer-hour-efficiency', from, to],
    queryFn: () => api.getTrailerHourEfficiency(from, to),
    enabled: !!from && !!to,
  });
}

export function useOperationalKPIs(from: string, to: string, filter?: { trailerId?: string; customerNumber?: string }) {
  return useQuery({
    queryKey: ['operational-kpis', from, to, filter],
    queryFn: () => api.getOperationalKPIs(from, to, filter),
    enabled: !!from && !!to,
  });
}

export function useRouteEconomics(from: string, to: string) {
  return useQuery({
    queryKey: ['route-economics', from, to],
    queryFn: () => api.getRouteEconomicsList(from, to),
    enabled: !!from && !!to,
  });
}

export function useRouteDetail(trailerId: string, routeId: string) {
  return useQuery({
    queryKey: ['route-detail', trailerId, routeId],
    queryFn: () => api.getRouteDetail(trailerId, routeId),
    enabled: !!trailerId && !!routeId,
  });
}

export function useDriverEconomics(from: string, to: string) {
  return useQuery({
    queryKey: ['driver-economics', from, to],
    queryFn: () => api.getDriverEconomicsList(from, to),
    enabled: !!from && !!to,
  });
}

export function useCustomerEconomics(from: string, to: string) {
  return useQuery({
    queryKey: ['customer-economics', from, to],
    queryFn: () => api.getCustomerEconomicsList(from, to),
    enabled: !!from && !!to,
  });
}

export function useOrderEconomics(from: string, to: string) {
  return useQuery({
    queryKey: ['order-economics', from, to],
    queryFn: () => api.getOrderEconomicsList(from, to),
    enabled: !!from && !!to,
  });
}

export function useRevenueByPeriod(from: string, to: string, groupBy: 'day' | 'week' | 'month' = 'day') {
  return useQuery({
    queryKey: ['revenue-by-period', from, to, groupBy],
    queryFn: () => api.getRevenueByPeriod(from, to, groupBy),
    enabled: !!from && !!to,
  });
}

// Calendar
export function useCalendarUtilization(from: string, to: string) {
  return useQuery({
    queryKey: ['calendar-utilization', from, to],
    queryFn: () => api.getCalendarUtilization(from, to),
    enabled: !!from && !!to,
  });
}

export function useCargoCalendar(from: string, to: string) {
  return useQuery({
    queryKey: ['cargo-calendar', from, to],
    queryFn: () => api.getCargoCalendar(from, to),
    enabled: !!from && !!to,
  });
}

// Organizations (Admin)
export function useOrganizations() {
  return useQuery({
    queryKey: ['organizations'],
    queryFn: () => api.getOrganizations(),
  });
}

export function useOrganization(id: string) {
  return useQuery({
    queryKey: ['organization', id],
    queryFn: () => api.getOrganization(id),
    enabled: !!id,
  });
}

export function useCreateOrganization() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Omit<Organization, 'id' | 'createdAt' | 'updatedAt'> & { doraApiKey: string }) =>
      api.createOrganization(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['organizations'] });
    },
  });
}

export function useUpdateOrganization() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<Organization> & { doraApiKey?: string } }) =>
      api.updateOrganization(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['organizations'] });
    },
  });
}

export function useDeleteOrganization() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.deleteOrganization(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['organizations'] });
    },
  });
}

// Users (Admin)
export function useUsers() {
  return useQuery({
    queryKey: ['users'],
    queryFn: () => api.getUsers(),
  });
}

export function useCreateUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { email: string; name: string; role?: 'SUPERADMIN' | 'ADMIN'; organizationId?: string; allowedMenuItems?: string[] }) =>
      api.createUser(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });
}

export function useUpdateUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: { email?: string; password?: string; name?: string; role?: 'SUPERADMIN' | 'ADMIN'; organizationId?: string | null; allowedMenuItems?: string[] } }) =>
      api.updateUser(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });
}

export function useDeleteUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.deleteUser(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });
}

// Org-level user management
export function useOrgUsers() {
  return useQuery({
    queryKey: ['org-users'],
    queryFn: () => api.getOrgUsers(),
  });
}

export function useCreateOrgUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { email: string; name: string; allowedMenuItems?: string[]; payrollAccess?: boolean }) =>
      api.createOrgUser(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['org-users'] });
    },
  });
}

export function useUpdateOrgUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: { email?: string; password?: string; name?: string; allowedMenuItems?: string[]; payrollAccess?: boolean } }) =>
      api.updateOrgUser(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['org-users'] });
    },
  });
}

export function useDeleteOrgUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.deleteOrgUser(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['org-users'] });
    },
  });
}

// Drivers
export function useDrivers() {
  return useQuery({
    queryKey: ['drivers'],
    queryFn: () => api.getDrivers(),
  });
}

// CVR
export function useCvrLookup() {
  return useMutation({
    mutationFn: (cvr: string) => api.lookupCvr(cvr),
  });
}

// Manual Costs
export function useCosts(params?: { trailerId?: string; routeId?: string; from?: string; to?: string }) {
  return useQuery({
    queryKey: ['costs', params],
    queryFn: () => api.getCosts(params),
  });
}

export function useCreateCost() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Omit<ManualCost, 'id' | 'organizationId' | 'createdAt'>) =>
      api.createCost(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['costs'] });
      queryClient.invalidateQueries({ queryKey: ['trailer-economics'] });
      queryClient.invalidateQueries({ queryKey: ['overview'] });
      queryClient.invalidateQueries({ queryKey: ['route-detail'] });
    },
  });
}

export function useDeleteCost() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.deleteCost(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['costs'] });
      queryClient.invalidateQueries({ queryKey: ['route-detail'] });
    },
  });
}

// Test API Key
export function useTestApiKey() {
  return useMutation({
    mutationFn: (organizationId: string) => api.testApiKey(organizationId),
  });
}

// Cost Categories
export function useCostCategories() {
  return useQuery({
    queryKey: ['cost-categories'],
    queryFn: () => api.getCostCategories(),
  });
}

export function useCreateCostCategory() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { key: string; name: string; color?: string }) =>
      api.createCostCategory(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cost-categories'] });
    },
  });
}

export function useUpdateCostCategory() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: { name?: string; color?: string; sortOrder?: number } }) =>
      api.updateCostCategory(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cost-categories'] });
    },
  });
}

export function useDeleteCostCategory() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.deleteCostCategory(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cost-categories'] });
    },
  });
}

// Suppliers
export function useSuppliers() {
  return useQuery({
    queryKey: ['suppliers'],
    queryFn: () => api.getSuppliers(),
  });
}

export function useCreateSupplier() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { name: string; cvr?: string | null }) =>
      api.createSupplier(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['suppliers'] });
    },
  });
}

export function useUpdateSupplier() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: { name?: string; cvr?: string | null } }) =>
      api.updateSupplier(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['suppliers'] });
    },
  });
}

export function useDeleteSupplier() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.deleteSupplier(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['suppliers'] });
    },
  });
}

// Cost Rules
export function useCostRules(params?: { trailerId?: string; isActive?: boolean }) {
  return useQuery({
    queryKey: ['cost-rules', params],
    queryFn: () => api.getCostRules(params),
  });
}

export function useCreateCostRule() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { name: string; description?: string | null; ruleType: string; category: string; rate: number; trailerId?: string | null; productNumbers?: string[]; isActive?: boolean; validFrom?: string | null; validTo?: string | null }) =>
      api.createCostRule(data as Parameters<typeof api.createCostRule>[0]),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cost-rules'] });
      queryClient.invalidateQueries({ queryKey: ['trailer-economics'] });
      queryClient.invalidateQueries({ queryKey: ['overview'] });
    },
  });
}

export function useUpdateCostRule() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Record<string, unknown> }) =>
      api.updateCostRule(id, data as Partial<import('../types').CostRule>),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cost-rules'] });
      queryClient.invalidateQueries({ queryKey: ['trailer-economics'] });
      queryClient.invalidateQueries({ queryKey: ['overview'] });
    },
  });
}

export function useDeleteCostRule() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.deleteCostRule(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cost-rules'] });
      queryClient.invalidateQueries({ queryKey: ['trailer-economics'] });
      queryClient.invalidateQueries({ queryKey: ['overview'] });
    },
  });
}

// Work Hours
export function useWorkHours(from: string, to: string) {
  return useQuery({
    queryKey: ['work-hours', from, to],
    queryFn: () => api.getWorkHours(from, to),
    enabled: !!from && !!to,
  });
}

export function useUpdateTrip() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ routeId, data }: { routeId: string; data: { driverId: string; pauseMinutes?: number; start?: { dateTime: string; odometer: string; note?: string | null }; end?: { dateTime: string; odometer: string; note?: string | null } } }) =>
      api.updateTrip(routeId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['work-hours'] });
    },
  });
}

export function useCreateWorkHourEntry() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Parameters<typeof api.createWorkHourEntry>[0]) =>
      api.createWorkHourEntry(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['work-hours'] });
    },
  });
}

export function useDeleteWorkHourEntry() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.deleteWorkHourEntry(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['work-hours'] });
    },
  });
}

export function useSyncWorkHours() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.syncWorkHours(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['work-hours'] });
    },
  });
}

export function useSyncAllWorkHours() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.syncAllWorkHours(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['work-hours'] });
    },
  });
}

// FMS Comparison
export function useFmsComparison(from: string, to: string, enabled: boolean) {
  return useQuery({
    queryKey: ['fms-comparison', from, to],
    queryFn: () => api.getFmsComparison(from, to),
    enabled: enabled && !!from && !!to,
  });
}

// Create Invite (Admin user with generated password)
export function useCreateInvite() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ organizationId, data }: { organizationId: string; data: { email: string; name: string } }) =>
      api.createInvite(organizationId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });
}

// Quotes
export function useQuotes(params?: { status?: string }) {
  return useQuery({
    queryKey: ['quotes', params],
    queryFn: () => api.getQuotes(params),
  });
}

export function useQuote(id: string) {
  return useQuery({
    queryKey: ['quote', id],
    queryFn: () => api.getQuote(id),
    enabled: !!id,
  });
}

export function useCreateQuote() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: import('../types').QuoteCreateInput) => api.createQuote(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['quotes'] });
    },
  });
}

export function useUpdateQuote() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<import('../types').QuoteCreateInput> }) =>
      api.updateQuote(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['quotes'] });
      queryClient.invalidateQueries({ queryKey: ['quote'] });
    },
  });
}

export function useDeleteQuote() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.deleteQuote(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['quotes'] });
    },
  });
}

export function useSendQuote() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.sendQuote(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['quotes'] });
      queryClient.invalidateQueries({ queryKey: ['quote'] });
    },
  });
}

export function useConvertQuote() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.convertQuote(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['quotes'] });
      queryClient.invalidateQueries({ queryKey: ['quote'] });
    },
  });
}

export function useAcceptQuote() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.acceptQuote(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['quotes'] });
      queryClient.invalidateQueries({ queryKey: ['quote'] });
    },
  });
}

export function useQuoteCustomers() {
  return useQuery({
    queryKey: ['quote-customers'],
    queryFn: () => api.getQuoteCustomers(),
  });
}

export function useQuoteTeams() {
  return useQuery({
    queryKey: ['quote-teams'],
    queryFn: () => api.getQuoteTeams(),
  });
}

export function useQuoteTeamCargoTypes(teamId: string | undefined | null) {
  return useQuery({
    queryKey: ['quote-team-cargo-types', teamId],
    queryFn: () => api.getQuoteTeamCargoTypes(teamId as string),
    enabled: !!teamId,
    staleTime: 5 * 60 * 1000,
  });
}

export function useQuoteLabels() {
  return useQuery({
    queryKey: ['quote-labels'],
    queryFn: () => api.getQuoteLabels(),
  });
}

export function useQuoteProducts() {
  return useQuery({
    queryKey: ['quote-products'],
    queryFn: () => api.getQuoteProducts(),
  });
}

export function useFollowupQuote() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.followupQuote(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['quotes'] });
      queryClient.invalidateQueries({ queryKey: ['quote'] });
    },
  });
}

export function useRejectQuote() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason, comment }: { id: string; reason?: string | null; comment?: string | null }) =>
      api.rejectQuote(id, reason, comment),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['quotes'] });
      queryClient.invalidateQueries({ queryKey: ['quote'] });
    },
  });
}

export function useUpdateQuoteNote() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, note }: { id: string; note: string | null }) => api.updateQuoteNote(id, note),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['quotes'] });
      queryClient.invalidateQueries({ queryKey: ['quote'] });
    },
  });
}

export function useQuoteCatalog() {
  return useQuery({
    queryKey: ['quote-catalog'],
    queryFn: () => api.getQuoteCatalog(),
  });
}

export function useSetQuoteCatalogCategory() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ doraProductId, category, defaultQuantity }: { doraProductId: string; category: string; defaultQuantity?: number | null }) =>
      api.setQuoteCatalogCategory(doraProductId, category, defaultQuantity),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['quote-catalog'] });
    },
  });
}

export function useQuoteTemplates(customerNumber?: string) {
  return useQuery({
    queryKey: ['quote-templates', customerNumber || 'all'],
    queryFn: () => api.getQuoteTemplates(customerNumber),
  });
}

export function useCreateQuoteTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: any) => api.createQuoteTemplate(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['quote-templates'] });
    },
  });
}

export function useDeleteQuoteTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.deleteQuoteTemplate(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['quote-templates'] });
    },
  });
}

// Order Schedules (Ordreskemaer)
export function useOrderSchedules(params?: { status?: string }) {
  return useQuery({
    queryKey: ['order-schedules', params],
    queryFn: () => api.getOrderSchedules(params),
  });
}

export function useOrderSchedule(id: string) {
  return useQuery({
    queryKey: ['order-schedule', id],
    queryFn: () => api.getOrderSchedule(id),
    enabled: !!id,
  });
}

export function useCreateOrderSchedule() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: import('../types').OrderScheduleInput) => api.createOrderSchedule(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['order-schedules'] });
    },
  });
}

export function useUpdateOrderSchedule() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: import('../types').OrderScheduleInput }) =>
      api.updateOrderSchedule(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['order-schedules'] });
      queryClient.invalidateQueries({ queryKey: ['order-schedule'] });
    },
  });
}

export function useDeleteOrderSchedule() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.deleteOrderSchedule(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['order-schedules'] });
    },
  });
}

export function usePauseOrderSchedule() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.pauseOrderSchedule(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['order-schedules'] });
      queryClient.invalidateQueries({ queryKey: ['order-schedule'] });
    },
  });
}

export function useResumeOrderSchedule() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.resumeOrderSchedule(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['order-schedules'] });
      queryClient.invalidateQueries({ queryKey: ['order-schedule'] });
    },
  });
}

export function useTriggerOrderSchedule() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.triggerOrderSchedule(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['order-schedules'] });
      queryClient.invalidateQueries({ queryKey: ['order-schedule'] });
    },
  });
}

export function useOrderScheduleCustomers() {
  return useQuery({
    queryKey: ['order-schedule-customers'],
    queryFn: () => api.getOrderScheduleCustomers(),
  });
}

export function useOrderScheduleTeams() {
  return useQuery({
    queryKey: ['order-schedule-teams'],
    queryFn: () => api.getOrderScheduleTeams(),
  });
}

export function useOrderScheduleLabels() {
  return useQuery({
    queryKey: ['order-schedule-labels'],
    queryFn: () => api.getOrderScheduleLabels(),
  });
}

export function useOrderScheduleProducts() {
  return useQuery({
    queryKey: ['order-schedule-products'],
    queryFn: () => api.getOrderScheduleProducts(),
  });
}

// Order Templates (within a schedule)
export function useOrderTemplates(scheduleId: string) {
  return useQuery({
    queryKey: ['orderTemplates', scheduleId],
    queryFn: () => api.getOrderTemplates(scheduleId),
    enabled: !!scheduleId,
  });
}

export function useOrderTemplate(scheduleId: string, templateId: string) {
  return useQuery({
    queryKey: ['orderTemplate', scheduleId, templateId],
    queryFn: () => api.getOrderTemplate(scheduleId, templateId),
    enabled: !!scheduleId && !!templateId,
  });
}

export function useCreateOrderTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ scheduleId, data }: { scheduleId: string; data: import('../types').OrderTemplateInput }) =>
      api.createOrderTemplate(scheduleId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['order-schedules'] });
      queryClient.invalidateQueries({ queryKey: ['order-schedule'] });
      queryClient.invalidateQueries({ queryKey: ['orderTemplates'] });
    },
  });
}

export function useUpdateOrderTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ scheduleId, templateId, data }: { scheduleId: string; templateId: string; data: import('../types').OrderTemplateInput }) =>
      api.updateOrderTemplate(scheduleId, templateId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['order-schedules'] });
      queryClient.invalidateQueries({ queryKey: ['order-schedule'] });
      queryClient.invalidateQueries({ queryKey: ['orderTemplates'] });
      queryClient.invalidateQueries({ queryKey: ['orderTemplate'] });
    },
  });
}

export function useDeleteOrderTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ scheduleId, templateId }: { scheduleId: string; templateId: string }) =>
      api.deleteOrderTemplate(scheduleId, templateId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['order-schedules'] });
      queryClient.invalidateQueries({ queryKey: ['order-schedule'] });
      queryClient.invalidateQueries({ queryKey: ['orderTemplates'] });
    },
  });
}

// ============ Payroll hooks ============

export function usePayrollCatalogs() {
  return useQuery({
    queryKey: ['payroll-catalogs'],
    queryFn: () => api.getPayrollCatalogs(),
  });
}

export function usePayrollRuleSets() {
  return useQuery({
    queryKey: ['payroll-rulesets'],
    queryFn: () => api.getPayrollRuleSets(),
  });
}

export function useCreateInheritedPayrollRuleSet() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ catalogKey, name, isDefault }: { catalogKey: string; name: string; isDefault: boolean }) =>
      api.createInheritedPayrollRuleSet(catalogKey, name, isDefault),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['payroll-rulesets'] }),
  });
}

export function useClonePayrollRuleSet() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.clonePayrollRuleSet(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['payroll-rulesets'] }),
  });
}

export function useUpdatePayrollRuleSet() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) => api.updatePayrollRuleSet(id, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['payroll-rulesets'] }),
  });
}

export function useUpdatePayrollRuleSetRate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ ruleSetId, rateId, data }: { ruleSetId: string; rateId: string; data: any }) =>
      api.updatePayrollRuleSetRate(ruleSetId, rateId, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['payroll-rulesets'] }),
  });
}

export function useDeletePayrollRuleSet() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.deletePayrollRuleSet(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['payroll-rulesets'] }),
  });
}

export function useDriverPayrollProfiles() {
  return useQuery({
    queryKey: ['payroll-profiles'],
    queryFn: () => api.getDriverPayrollProfiles(),
  });
}

export function useUpsertDriverPayrollProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ driverId, data }: { driverId: string; data: any }) =>
      api.upsertDriverPayrollProfile(driverId, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['payroll-profiles'] });
      qc.invalidateQueries({ queryKey: ['payroll-breakdown'] });
    },
  });
}

export function usePayrollBreakdownAll(from: string, to: string) {
  return useQuery({
    queryKey: ['payroll-breakdown-all', from, to],
    queryFn: () => api.getPayrollBreakdownAll(from, to),
    enabled: !!from && !!to,
  });
}

export function usePayrollBreakdown(driverId: string, from: string, to: string) {
  return useQuery({
    queryKey: ['payroll-breakdown', driverId, from, to],
    queryFn: () => api.getPayrollBreakdown(driverId, from, to),
    enabled: !!driverId && !!from && !!to,
  });
}

export function useCreatePayrollManualEntry() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: any) => api.createPayrollManualEntry(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['payroll-breakdown'] });
      qc.invalidateQueries({ queryKey: ['payroll-breakdown-all'] });
      qc.invalidateQueries({ queryKey: ['payroll-manual-entries'] });
    },
  });
}

export function useDeletePayrollManualEntry() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.deletePayrollManualEntry(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['payroll-breakdown'] });
      qc.invalidateQueries({ queryKey: ['payroll-breakdown-all'] });
      qc.invalidateQueries({ queryKey: ['payroll-manual-entries'] });
    },
  });
}

export function useLockPayrollPeriod() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { driverId: string; periodStart: string; periodEnd: string; periodType?: 'WEEK' | 'MONTH' }) =>
      api.lockPayrollPeriod(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['payroll-snapshots'] });
    },
  });
}

export function usePayrollSnapshots(from?: string, to?: string) {
  return useQuery({
    queryKey: ['payroll-snapshots', from, to],
    queryFn: () => api.getPayrollSnapshots(from, to),
  });
}

// --- Produktsalg / Sales locations ---

export function useSalesLocations() {
  return useQuery({
    queryKey: ['sales-locations'],
    queryFn: () => api.getSalesLocations(),
  });
}

export function useCreateSalesLocation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: import('../types').SalesLocationInput) => api.createSalesLocation(data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sales-locations'] }),
  });
}

export function useUpdateSalesLocation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<import('../types').SalesLocationInput> }) =>
      api.updateSalesLocation(id, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sales-locations'] }),
  });
}

export function useDeleteSalesLocation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.deleteSalesLocation(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sales-locations'] }),
  });
}

export function useAddSalesLocationProduct() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ locationId, data }: { locationId: string; data: import('../types').SalesLocationProductInput }) =>
      api.addSalesLocationProduct(locationId, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sales-locations'] }),
  });
}

export function useUpdateSalesLocationProduct() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ locationId, productId, data }: { locationId: string; productId: string; data: Partial<import('../types').SalesLocationProductInput> }) =>
      api.updateSalesLocationProduct(locationId, productId, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sales-locations'] }),
  });
}

export function useDeleteSalesLocationProduct() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ locationId, productId }: { locationId: string; productId: string }) =>
      api.deleteSalesLocationProduct(locationId, productId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sales-locations'] }),
  });
}

export function useSalesLocationsReport(from: string, to: string) {
  return useQuery({
    queryKey: ['sales-locations-report', from, to],
    queryFn: () => api.getSalesLocationsReport(from, to),
    enabled: !!from && !!to,
  });
}

export function useCustomerPrices() {
  return useQuery({
    queryKey: ['customer-prices'],
    queryFn: () => api.getCustomerPrices(),
  });
}

export function useDeleteCustomerPrice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.deleteCustomerPrice(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['customer-prices'] }),
  });
}

export function useUpsertCustomerPriceBulk() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: Parameters<typeof api.upsertCustomerPriceBulk>[0]) => api.upsertCustomerPriceBulk(input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['customer-prices'] }),
  });
}

export function useDeleteCustomerPriceBulk() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ customerNumber, doraProductId }: { customerNumber: string; doraProductId: string }) =>
      api.deleteCustomerPriceBulk(customerNumber, doraProductId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['customer-prices'] }),
  });
}

// ============ Price Groups ============
export function usePriceGroups() {
  return useQuery({
    queryKey: ['price-groups'],
    queryFn: () => api.getPriceGroups(),
  });
}
export function usePriceGroup(id: string | undefined) {
  return useQuery({
    queryKey: ['price-group', id],
    queryFn: () => api.getPriceGroup(id as string),
    enabled: !!id,
  });
}
export function useCreatePriceGroup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: import('../types').PriceGroupInput) => api.createPriceGroup(input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['price-groups'] }),
  });
}
export function useUpdatePriceGroup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: Partial<import('../types').PriceGroupInput> }) => api.updatePriceGroup(id, input),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['price-groups'] });
      qc.invalidateQueries({ queryKey: ['price-group', vars.id] });
    },
  });
}
export function useDeletePriceGroup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.deletePriceGroup(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['price-groups'] }),
  });
}
export function useAddPriceGroupCustomers() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ groupId, customerNumbers }: { groupId: string; customerNumbers: string[] }) =>
      api.addPriceGroupCustomers(groupId, customerNumbers),
    onSuccess: (_, vars) => qc.invalidateQueries({ queryKey: ['price-group', vars.groupId] }),
  });
}
export function useRemovePriceGroupCustomer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ groupId, customerNumber }: { groupId: string; customerNumber: string }) =>
      api.removePriceGroupCustomer(groupId, customerNumber),
    onSuccess: (_, vars) => qc.invalidateQueries({ queryKey: ['price-group', vars.groupId] }),
  });
}

// ============ Sales-location merges ============
export function useMergeSalesLocation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ sourceId, targetId }: { sourceId: string; targetId: string }) =>
      api.mergeSalesLocation(sourceId, targetId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sales-locations'] });
      qc.invalidateQueries({ queryKey: ['price-groups'] });
    },
  });
}
